// ARCA-92 / WS1 — post-redirect "return to" stash.
//
// When the router-level auth guard (router.tsx) bounces a guest off an
// auth-required route to /splash, it records the intended destination here so
// a later sign-in (WS2) can resume the user back to where they were headed.
//
// In-memory only (plain atom, NOT persisted): the redirect → sign-in round
// trip happens inside a single tab session, and we deliberately do NOT want a
// stale destination surviving a reload or a fresh cold visit. WS2 consumes and
// clears this after a successful login.
//
// Pattern matches the single-atom store files in this folder (e.g.
// `$mulliganActive`, `$cascadeFrontier`): callers `.set(…)` / `.get()` the
// atom directly. The WS2 read path is `consumeReturnTo()` below.

import { atom } from 'nanostores'

/**
 * Path a guest was trying to reach when the auth guard redirected them to
 * /splash, or null when there is no pending destination. WS2 reads this on
 * sign-in to resume navigation, then resets it to null.
 */
export const $returnTo = atom<string | null>(null)

/** Default landing route when there is no pending destination. */
export const DEFAULT_POST_LOGIN_ROUTE = '/lobby'

/**
 * Read-and-clear the stashed destination, returning where a just-signed-in user
 * should land (ARCA-93 / WS2). Defaults to {@link DEFAULT_POST_LOGIN_ROUTE} when
 * nothing was stashed.
 *
 * Safety: the guard only ever stashes an in-app router path (begins with a
 * single `/`). We still reject anything that is not a same-origin path — most
 * importantly a protocol-relative `//host` URL, which `history.pushState`
 * would treat as cross-origin — so a corrupted/unexpected value can never
 * redirect the user off-site. Always clears the atom, valid or not.
 */
export function consumeReturnTo(): string {
  const dest = $returnTo.get()
  $returnTo.set(null)
  if (dest?.startsWith('/') && !dest.startsWith('//')) return dest
  return DEFAULT_POST_LOGIN_ROUTE
}
